import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { VIEW } from "const"
import { replaceHistoryState, forceRerenderProjectContent } from "client/utils"
import {
    StoreType,
    setUrlOrigin,
    renderProjectContent,
} from "client/store"
import {
    ProjectRouteItem,
    projectsRoute,
    projectDetailsRoute,
} from "client/parts/routes"
import {
    ProjectMenuColumn,
    ProjectMenuContent,
} from "client/parts/projectLayout"
import { Link } from "client/components"
import {
    ProjectMenuItem,
    ProjectMenuSubItemWrapper,
    ProjectMenuSubItem,
    ProjectMenuItemTitle,
    ProjectMenuItemArrow,
} from "./styled"


interface ExpandedRoutes { [path: string]: boolean }

interface ProjectMenuProps {
    routes: ProjectRouteItem[]
}

const ProjectMenu = ({
    routes = null,
}: ProjectMenuProps) => {
    const [activeItem, setActiveItem] = useState<string>(null)
    const [activeSubItem, setActiveSubItem] = useState<string>(null)
    const [expandedRoutes, setExpandedRoutes] = useState<ExpandedRoutes>({})

    const project = useSelector((state: StoreType) => state.project.projectView)
    const dispatch = useDispatch()
    const baseProjectRoute = `${projectsRoute}/${VIEW}/${project.id}`

    useEffect(() => {
        parseRoute()
        if (project && project.id) {
            dispatch(renderProjectContent(true))
        }
    }, [project])

    const parseRoute = () => {
        let { pathname } = window.location
        if (pathname === baseProjectRoute) {
            pathname = `${baseProjectRoute}${projectDetailsRoute}`
            replaceHistoryState(pathname)
        }
        dispatch(setUrlOrigin(pathname))

        const expanded: ExpandedRoutes = {}
        routes.forEach((route) => {
            const itemRoute = `${baseProjectRoute}${route.path}`
            if (pathname.indexOf(itemRoute) === 0) {
                if (route.routes) {
                    expanded[itemRoute] = true
                    setExpandedRoutes(expanded)

                    route.routes.forEach((subRoute) => {
                        const subItemRoute = `${baseProjectRoute}${subRoute.path}`
                        if (pathname.indexOf(subItemRoute) === 0) {
                            setActiveSubItem(subItemRoute)
                        }
                    })
                } else {
                    setActiveItem(itemRoute)
                }
            }
        })
    }

    const onClickRoute = (route: ProjectRouteItem) => {
        const itemRoute = `${baseProjectRoute}${route.path}`
        if (route.routes) {
            const expanded: ExpandedRoutes = { ...expandedRoutes }
            if (expanded[itemRoute]) expanded[itemRoute] = false
            else expanded[itemRoute] = true
            setExpandedRoutes(expanded)
        } else {
            setActiveItem(itemRoute)
            setActiveSubItem(null)

            forceRerenderProjectContent(itemRoute, dispatch)
        }
    }

    const onClickSubRoute = (route: ProjectRouteItem) => {
        const subItemRoute = `${baseProjectRoute}${route.path}`
        setActiveItem(null)
        setActiveSubItem(subItemRoute)

        forceRerenderProjectContent(subItemRoute, dispatch)
    }

    return (
        <ProjectMenuColumn>
            <ProjectMenuContent>
                {
                    routes.map((route) => {
                        if (!route.access) return ""

                        const itemRoute = `${baseProjectRoute}${route.path}`
                        const isExpanded = !!expandedRoutes[itemRoute]
                        return ([
                            <ProjectMenuItem
                                key={`route_${itemRoute}`}
                                fitContent={route.fitContent}
                                isActive={activeItem === itemRoute}
                            >
                                <Link
                                    href={itemRoute}
                                    onClick={() => onClickRoute(route)}
                                >
                                    {
                                        typeof route.title === "string"
                                            ? <ProjectMenuItemTitle>{route.title}</ProjectMenuItemTitle>
                                            : route.title
                                    }
                                    {
                                        route.routes
                                        && <ProjectMenuItemArrow isExpanded={isExpanded} />
                                    }
                                </Link>
                            </ProjectMenuItem>,

                            route.routes
                            && (
                                <ProjectMenuSubItemWrapper
                                    key={`subRoutes_${itemRoute}`}
                                    isExpanded={isExpanded}
                                    childrenLength={route.routes.filter((subRoute) => subRoute.access).length}
                                >
                                    {
                                        route.routes.map((subRoute) => {
                                            if (!subRoute.access) return ""
                                            const subItemRoute = `${baseProjectRoute}${subRoute.path}`
                                            return (
                                                <ProjectMenuSubItem
                                                    key={subItemRoute}
                                                    isActive={activeSubItem === subItemRoute}
                                                >
                                                    <Link
                                                        href={subItemRoute}
                                                        onClick={() => onClickSubRoute(subRoute)}
                                                    >
                                                        {
                                                            typeof subRoute.title === "string"
                                                                ? <ProjectMenuItemTitle>{subRoute.title}</ProjectMenuItemTitle>
                                                                : subRoute.title
                                                        }
                                                    </Link>
                                                </ProjectMenuSubItem>
                                            )
                                        })
                                    }
                                </ProjectMenuSubItemWrapper>
                            ),
                        ])
                    })
                }
            </ProjectMenuContent>
        </ProjectMenuColumn>
    )
}
export default ProjectMenu
